import type { ScheduleGrid } from "features/transient-response/schedules/model/types";

import { timeValueToHour } from "features/shared/lib/formatters";
import { useCallback, useEffect, useMemo, useState } from "react";

import type {
  RoomScheduleHeatmapKey,
  RoomScheduleHeatmapState,
  RoomScheduleMode,
  RoomScheduleRealisticCatalog,
  RoomSchedulesApi,
  RoomScheduleUsageHours,
} from "../model/scheduleTypes";

import {
  buildRoomSchedulesPayload,
  copyGridToCustom,
  defaultRoomSchedulesState,
  lookupRealisticSchedule,
  mapApiSchedulesToState,
  resolveCustomScheduleGrid,
  scheduleMatrixToGrid,
} from "../lib/roomScheduleApi";
import { createDinScheduleGrid, createEmptyScheduleGrid, isScheduleGridPopulated } from "../lib/roomSchedulePresets";

type UseRoomSchedulesOptions = {
  catalog: RoomScheduleRealisticCatalog;
  enabled: boolean;
  initialSchedules?: null | RoomSchedulesApi;
  isLoading: boolean;
  resetKey?: string;
  roomNames: readonly (string | undefined)[];
  usageEndCet: string;
  usageSaturday: boolean;
  usageStartCet: string;
  usageSunday: boolean;
};

const DEFAULT_USAGE_START_HOUR = 7;
const DEFAULT_USAGE_END_HOUR = 18;

const toUsageHours = (
  usageStartCet: string,
  usageEndCet: string,
  usageSaturday: boolean,
  usageSunday: boolean
): RoomScheduleUsageHours => ({
  usageEndHour: timeValueToHour(usageEndCet) ?? DEFAULT_USAGE_END_HOUR,
  usageSaturday,
  usageStartHour: timeValueToHour(usageStartCet) ?? DEFAULT_USAGE_START_HOUR,
  usageSunday,
});

export const useRoomSchedules = ({
  catalog,
  enabled,
  initialSchedules,
  isLoading,
  resetKey,
  roomNames,
  usageEndCet,
  usageSaturday,
  usageStartCet,
  usageSunday,
}: UseRoomSchedulesOptions) => {
  const [schedules, setSchedules] = useState(defaultRoomSchedulesState);

  useEffect(() => {
    if (!enabled || isLoading) return;
    setSchedules(mapApiSchedulesToState(initialSchedules));
  }, [enabled, isLoading, resetKey]);

  const usage = useMemo(
    () => toUsageHours(usageStartCet, usageEndCet, usageSaturday, usageSunday),
    [usageEndCet, usageSaturday, usageStartCet, usageSunday]
  );

  const resolveGrid = useCallback(
    (key: RoomScheduleHeatmapKey): ScheduleGrid => {
      const item = schedules[key];
      if (item.mode === "custom") return resolveCustomScheduleGrid(item.customGrid);
      if (item.mode === "din") return createDinScheduleGrid(usage);
      const realistic = lookupRealisticSchedule(catalog, key, roomNames);
      return realistic?.data ? scheduleMatrixToGrid(realistic.data) : createEmptyScheduleGrid();
    },
    [catalog, roomNames, schedules, usage]
  );

  const hasData = useCallback(
    (key: RoomScheduleHeatmapKey) => isScheduleGridPopulated(resolveGrid(key)),
    [resolveGrid]
  );

  const updateSchedule = useCallback((key: RoomScheduleHeatmapKey, patch: Partial<RoomScheduleHeatmapState>) => {
    setSchedules((current) => ({ ...current, [key]: { ...current[key], ...patch } }));
  }, []);

  const setMode = useCallback((key: RoomScheduleHeatmapKey, mode: RoomScheduleMode) => {
    setSchedules((current) => {
      const item = current[key];
      const customGrid = mode === "custom" ? resolveCustomScheduleGrid(item.customGrid) : item.customGrid;

      return { ...current, [key]: { ...item, customGrid, mode } };
    });
  }, []);

  const editAsCustom = useCallback(
    (key: RoomScheduleHeatmapKey) => {
      setSchedules((current) => ({ ...current, [key]: copyGridToCustom(resolveGrid(key)) }));
    },
    [resolveGrid]
  );

  const getPayload = useCallback(() => buildRoomSchedulesPayload(schedules, resolveGrid), [resolveGrid, schedules]);

  return {
    editAsCustom,
    getPayload,
    hasData,
    resolveGrid,
    schedules,
    setMode,
    updateSchedule,
  };
};

export type UseRoomSchedulesReturn = ReturnType<typeof useRoomSchedules>;
